/** Loading estimates.
 *
 * The index (one entry per security) is fetched once. Individual securities are
 * then read out of the binary shards with a single HTTP range request each —
 * about 8 KB for the average security — so adding a fifth line to a chart costs
 * one small round trip rather than a re-download of the panel.
 */

import type { FirmIndex, FirmRecord, Manifest, SecurityRef } from "./types";

const BASE = (import.meta.env.VITE_DATA_BASE ?? "/data").replace(/\/$/, "");

let manifestPromise: Promise<Manifest> | null = null;
let indexPromise: Promise<FirmIndex> | null = null;

export function loadManifest(): Promise<Manifest> {
  manifestPromise ??= fetch(`${BASE}/manifest.json`).then(failFast).then((r) => r.json());
  return manifestPromise;
}

export function loadFirmIndex(): Promise<FirmIndex> {
  indexPromise ??= fetch(`${BASE}/firms/index.json`).then(failFast).then((r) => r.json());
  return indexPromise;
}

function failFast(response: Response): Response {
  if (!response.ok) throw new Error(`${response.status} ${response.statusText} — ${response.url}`);
  return response;
}

export function securityList(index: FirmIndex): SecurityRef[] {
  return index.records.map(([t0, t1], id) => ({
    id,
    ...(index.labels
      ? { name: index.labels[id][0], ticker: index.labels[id][1] || undefined }
      : { permno: index.permnos?.[id] }),
    start: index.months[t0],
    end: index.months[t1],
  }));
}

/** How a security is shown wherever there is no room for more. */
export function securityLabel(ref: SecurityRef | undefined, id: number): string {
  return ref?.name ?? ref?.ticker ?? (ref?.permno ? `PERMNO ${ref.permno}` : `#${id}`);
}

const recordCache = new Map<number, Promise<FirmRecord>>();

export function loadSecurity(index: FirmIndex, id: number): Promise<FirmRecord> {
  const cached = recordCache.get(id);
  if (cached) return cached;

  const entry = index.records[id];
  if (!entry) return Promise.reject(new Error(`Security ${id} is not in the panel`));

  const [t0, t1, shard, offset] = entry;
  const span = t1 - t0 + 1;
  const width = span * index.bytesPerValue;
  const bytes = index.series.length * width;

  const promise = fetch(`${BASE}/firms/shard-${String(shard).padStart(2, "0")}.bin`, {
    headers: { Range: `bytes=${offset}-${offset + bytes - 1}` },
  })
    .then(failFast)
    .then((response) => response.arrayBuffer().then((buffer) => ({ response, buffer })))
    .then(({ response, buffer }) => {
      // A host that honours the Range header replies 206 and the body begins at
      // the record. A host that ignores it replies 200 with the whole shard --
      // Cloudflare Pages does exactly that, despite advertising
      // `accept-ranges: bytes` -- and then the record begins at `offset`.
      // Assuming the former silently reads a *different* security's bytes and
      // plots them without erroring, so the status is checked, never assumed.
      const base = response.status === 206 ? 0 : offset;
      if (buffer.byteLength < base + bytes) {
        throw new Error(
          `Short read for security ${id}: wanted ${bytes} bytes at ${base}, got a ` +
            `${buffer.byteLength}-byte body (HTTP ${response.status}).`,
        );
      }
      const series = new Map<string, Float32Array>();
      index.series.forEach((meta, i) => {
        series.set(meta.id, new Float32Array(buffer, base + i * width, span));
      });
      return {
        id,
        ...(index.labels
          ? { name: index.labels[id][0], ticker: index.labels[id][1] || undefined }
          : { permno: index.permnos?.[id] }),
        months: index.months.slice(t0, t1 + 1),
        series,
      } satisfies FirmRecord;
    });

  recordCache.set(id, promise);
  promise.catch(() => recordCache.delete(id));
  return promise;
}

/** Rank securities for the search box: exact ticker, then prefix, then substring.
 *  PERMNO is matched only on local builds that have no company names, since it
 *  is a licensed identifier and is not published otherwise. */
export function searchSecurities(all: SecurityRef[], query: string, limit = 40): SecurityRef[] {
  const q = query.trim().toUpperCase();
  if (!q) return [];

  const scored: { ref: SecurityRef; score: number }[] = [];
  for (const ref of all) {
    const ticker = ref.ticker ?? "";
    const name = (ref.name ?? "").toUpperCase();
    const permno = ref.permno ? String(ref.permno) : "";

    let score = Infinity;
    if (ticker && ticker === q) score = 0;
    else if (permno && permno === q) score = 1;
    else if (ticker.startsWith(q)) score = 2;
    else if (name.startsWith(q)) score = 3;
    else if (permno.startsWith(q)) score = 4;
    else if (name.includes(q)) score = 5;

    if (score < Infinity) scored.push({ ref, score });
  }

  scored.sort((a, b) => a.score - b.score || b.ref.end - a.ref.end || a.ref.id - b.ref.id);
  return scored.slice(0, limit).map((s) => s.ref);
}
